import { IconButton, mergeStyles, MessageBar, MessageBarType, Stack, Text, useTheme } from '@fluentui/react';
import { RootState } from 'app/store';
import CustomPanel from 'components/controls/CustomPanel';
import { emitCustomEvent, fadeIn } from 'global/Application';
import { useApplication } from 'global/Application/Context';
import { useAuthentication } from 'global/Authentication';
import { ComparisonViewModelGenerator, ECompareVMStatus, IEntityVM, IPrimePharmacyComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import { IGCDataLight } from 'global/Reducer/dataSlice';
import { setProcessing } from 'global/Reducer/statusSlice';
import { useApplicationTheme } from 'global/themes';
import { Delay, ScrollToPositionCascaded } from 'global/Utility/TSFunctions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    CONST_MARKER_OFF,
    CONST_MARKER_OFF_DARK,
    CONST_MARKER_ON,
    getDefaultCollapsed,
    IMarkerSet,
    ORIGIN_TITLE,
    shouldIncludeComparisonItem,
    STACK_DEFAULT_PROPS,
    STICKY_HEADER_HEIGHT,
    TARGET_TITLE,
} from 'views/BlueRelationship/Compare/Compare.constants';
import CompareRow from 'views/BlueRelationship/Compare/CompareRow';
import { CompareTitle } from 'views/BlueRelationship/Compare/CompareTitle';
import ComparePanel, { EPanelStyleType } from 'views/BlueRelationship/Compare/CustomPanel';
import EntityComparePanel from 'views/BlueRelationship/Compare/EntityComparePanel';
import FlexibleFieldView, { EScopeType } from 'views/BlueRelationship/FlexibleFieldView';
import GroupStructureGroupNumberView from 'views/BlueRelationship/GroupStructureGroupNumberView';
import { IPharmacyPrimeViewModel } from '../Models/IViewModel';
import { BuildPrimeData, IPrimeData } from '../PrimeHelpers';
import CompareTable from './PharmacyCompareTable';
import {
    BENEFIT_COMPARISON_PANEL_ID,
    GROUP_NUMBER_COMPARISON_PANEL_ID,
    QUICK_NAV_OVERLAY_VISIBLE_STYLE,
    TABLE_STYLES
} from './PharmacyPlan.constants';
import PharmacyQuickNavOverlay from './PharmacyQuickNavOverlay';
import PharmacyRadialNavOverlay from './PharmacyRadialNavOverlay';
import { IPharmacyPlanContextProps } from './PharmacyPlanContext';

interface IProps {
    viewModel: IPharmacyPrimeViewModel;
    context: IPharmacyPlanContextProps;
}

// pharmacy compare is read-only: no comments and no merge actions

const PharmacyPlan: React.FC<IProps> = (props) => {

    const state = useSelector((state: RootState) => state);
    const application = useApplication();
    const theme = useTheme();
    const appTheme = useApplicationTheme();
    const dispatch = useDispatch();
    const authentication = useAuthentication();

    const [comparisonState, setComparisonState] = application.comparisonState;

    const [famModel, setFAMModel] = useState<IPrimeData | null>(null);

    const [compareViewModel, setCompareViewModel] = useState<IPrimePharmacyComparisonVM | null>(null);

    const configuration = comparisonState?.configuration ?? null;
    const showDifferencesOnly = configuration?.showDifferencesOnly ?? false;
    const hasUnsavedChanges = (state.data.modifiedOn ?? -1) > (state.data.savedOn ?? 0);

    useEffect(() => {

        // run the comparison and generate the view models for the comparison, 
        // this is a very expensive operation so we need to be careful with it, 
        // we only want to run it when the compareState or the pharmacyExtract changes
        const runAsync = async () => {
            try {

                if (!props.viewModel.pharmacyExtract)
                    return;

                if (!state)
                    return;

                if (!comparisonState) {

                    const primeState: IGCDataLight = {
                        account: state.data.account!,
                        config: state.data.config,
                        json: structuredClone(state.data.json),
                        snapshot: structuredClone(state.data.snapshot),
                    };

                    const nTarget = await BuildPrimeData(
                        primeState,
                        props.viewModel.pharmacyExtract.id);

                    setFAMModel(nTarget);

                } else if (comparisonState?.origin && comparisonState?.target) {

                    const pharmacyExtractId = props.viewModel.pharmacyExtract.id;

                    const nOrigin = await BuildPrimeData(comparisonState.origin, pharmacyExtractId);
                    const nTarget = await BuildPrimeData(comparisonState.target, pharmacyExtractId);


                    const viewModel = await ComparisonViewModelGenerator.GeneratePrimePharmacyConfigurationModel(
                        nOrigin,
                        nTarget,
                        props.viewModel.primeCategory,
                        props.viewModel.primeBenefit.id,
                        state.data.config
                    );

                    setCompareViewModel(viewModel);

                }
            } catch (err: any) {
                emitCustomEvent({
                    eventType: 'globalException',
                    details: err
                });
                console.error(err);
            } finally {
                dispatch(setProcessing(false))
            }
        };

        runAsync();

    }, [comparisonState, props.viewModel.pharmacyExtract, state]);

    const markers = useMemo<IMarkerSet>(() => ({
        MARKER_OFF: appTheme.theme === 'dark' ? CONST_MARKER_OFF : CONST_MARKER_OFF_DARK,
        MARKER_ON: CONST_MARKER_ON,
    }), [appTheme.theme]);

    const [quickNavIsHidden, setQuickNavIsHidden] = useState(true);
    const [radialNavIsHidden, setRadialNavIsHidden] = useState(true);


    const currentChangeIndexRef = useRef<number>(-1);
    const activeChangeElementRef = useRef<HTMLElement | null>(null);
    const getChangeElements = useCallback((): HTMLElement[] => {
        const allChangeElements = Array.from(document.querySelectorAll<HTMLElement>('tr.highlight'));

        if (allChangeElements.length === 0)
            return [];

        const visibleChangeElements = allChangeElements.filter((element) => element.getClientRects().length > 0);
        return visibleChangeElements.length > 0 ? visibleChangeElements : allChangeElements;
    }, []);

    const focusChangeElement = useCallback((elements: HTMLElement[], index: number) => {
        const element = elements[index];

        if (!element)
            return;

        if (activeChangeElementRef.current) {
            activeChangeElementRef.current.style.outline = '';
            activeChangeElementRef.current.style.boxShadow = '';
        }

        activeChangeElementRef.current = element;

        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.outline = '2px solid #f5d020';
        element.style.boxShadow = '0 0 0 2px rgba(245, 208, 32, 0.35)';

        window.setTimeout(() => {
            if (activeChangeElementRef.current === element) {
                element.style.outline = '';
                element.style.boxShadow = '';
            }
        }, 1500);
    }, []);

    const navigateChanges = useCallback((direction: 1 | -1) => {
        const changeElements = getChangeElements();

        if (changeElements.length === 0)
            return;

        let nextIndex = currentChangeIndexRef.current + direction;

        if (nextIndex >= changeElements.length)
            nextIndex = 0;

        if (nextIndex < 0)
            nextIndex = changeElements.length - 1;

        currentChangeIndexRef.current = nextIndex;
        focusChangeElement(changeElements, nextIndex);
    }, [focusChangeElement, getChangeElements]);

    const findNextChange = useCallback(() => {
        navigateChanges(1);
    }, [navigateChanges]);

    const findPreviousChange = useCallback(() => {
        navigateChanges(-1);
    }, [navigateChanges]);

    // Save on Ctrl+S or Cmd+S
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.shiftKey && e.ctrlKey && !e.altKey) {
                setQuickNavIsHidden(t => !t);
                return;
            }

            if (e.shiftKey && e.altKey && !e.ctrlKey) {
                setRadialNavIsHidden(t => !t);
                return;
            }

            if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key === 'ArrowDown') {
                e.preventDefault();
                findNextChange();
                return;
            }

            if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key === 'ArrowUp') {
                e.preventDefault();
                findPreviousChange();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [findNextChange, findPreviousChange]);

    useEffect(() => {
        currentChangeIndexRef.current = -1;

        if (activeChangeElementRef.current) {
            activeChangeElementRef.current.style.outline = '';
            activeChangeElementRef.current.style.boxShadow = '';
            activeChangeElementRef.current = null;
        }
    }, [compareViewModel, showDifferencesOnly]);

    useEffect(() => {
        // when quick nav or radial nav is open, prevent body scroll, when it's closed, enable body scroll
        document.body.style.overflow = quickNavIsHidden && radialNavIsHidden ? 'auto' : 'hidden';

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [quickNavIsHidden, radialNavIsHidden]);

    const classNames = useMemo(() => {
        return mergeStyles({
            "td": {},
            "tr": {
                "& .highlight": {
                    ":nth-child(even)": {
                        backgroundColor: theme.isInverted ? 'rgb(67, 53, 25)' : 'rgb(240, 230, 200)',
                    },
                    ":nth-child(odd)": {
                        backgroundColor: theme.isInverted ? 'rgb(67, 65, 35)' : 'rgb(255, 250, 221)',
                    },
                },
                ":hover": {
                    backgroundColor: theme.semanticColors.warningBackground,
                },
            },
            "& > :nth-child(even)": {
                backgroundColor: theme.semanticColors.bodyDivider,
            },
        });
    }, [theme]);

    const handleCancelCompare = useCallback(() => {
        setComparisonState(null);
        setCompareViewModel(null);
    }, []);

    const handleQuickNavNavigate = useCallback(async (links: string[]) => {
        setQuickNavIsHidden(true);
        await Delay(100);
        await ScrollToPositionCascaded(links);
    }, []);

    const onRenderPrimeFAMSummary = () => {

        if (!famModel)
            return <></>;

        const summaryVM = (() => {
            return ComparisonViewModelGenerator.GeneratePropertiesModel(
                {} as any,
                famModel.summary ?? {} as any,
                () => ComparisonViewModelGenerator.PrimePharmacyPropertyDefinition
            );
        })();

        return (
            <CustomPanel title={"Summary"}
                canCollapse={true}
                defaultCollapsed={false}
                storageId={"pharmacy.summary"}
            >
                <CompareTable
                    className={classNames}
                    tableStyles={TABLE_STYLES}
                    targetHeader={"Value"}
                    hideOriginHeader={true}
                    hideActionHeader={true}
                >
                    {summaryVM.rowsVM.map((property) => {
                        const name = property.propertyDefinition.name;

                        return (
                            <tr key={property.propertyDefinition.name}>
                                <td style={TABLE_STYLES.tbodyColName}>
                                    <Text styles={TABLE_STYLES.headerStyles}>
                                        {name}
                                    </Text>
                                </td>
                                <td style={TABLE_STYLES.tbodyColTarget}>
                                    <Text>
                                        {property.target ?? "---"}
                                    </Text>
                                </td>
                            </tr>
                        );
                    })}
                </CompareTable>
            </CustomPanel>
        );
    }

    const onRenderFAMAttributes = () => {

        if (!famModel?.primeBenefit?.bcbs_config_categoryid)
            return <></>;

        const category = state.data.config.cache?.map_bcbs_config_category[famModel?.primeBenefit?.bcbs_config_categoryid];

        return (
            <CustomPanel title={`Benefit`}
                canCollapse={true}
                defaultCollapsed={false}
                storageId={"pharmacy.attributes"}
            >
                <FlexibleFieldView
                    fields={famModel?.primeBenefit?.fields ?? []}
                    category={category}
                    scope={famModel?.primeBenefit}
                    scopeType={EScopeType.benefit}
                    tiers={[]}
                    disabled={true}
                />
            </CustomPanel>
        );
    }

    const onRenderPrimeFAMGroupNumbers = () => {
        return <CustomPanel title={"Group Structure"}
            canCollapse={true}
            defaultCollapsed={false}
            id={"panel-group-number-comparison"}
        >
            <GroupStructureGroupNumberView
                currentPlan={props.viewModel?.plan}
                disabled={true}
            />
        </CustomPanel>;
    }

    const renderFAMView = () => {
        // Loading has not completed
        if (!famModel?.primeBenefit)
            return <MessageBar className={fadeIn} messageBarType={MessageBarType.warning} styles={{ root: { marginTop: '10px' } }}>
                No Prime pharmacy found.
            </MessageBar>

        return (
            <Stack>
                {onRenderPrimeFAMSummary()}
                {onRenderFAMAttributes()}
                {onRenderPrimeFAMGroupNumbers()}
            </Stack>
        );
    }

    const renderButtonQuickNavBookmark = () => {
        return <IconButton
            iconProps={{ iconName: 'DoubleBookmark' }}
            title="Quick Navigation (Ctrl + Shift)"
            ariaLabel="Quick Navigation (Ctrl + Shift)"
            onClick={() => setQuickNavIsHidden(false)}
            styles={{
                icon: {
                    fontSize: '16px',
                },
                root: {
                    zIndex: (QUICK_NAV_OVERLAY_VISIBLE_STYLE.zIndex as any) - 1,
                    backgroundColor: theme.palette.white,
                    border: '1px solid #20202020',
                    borderRadius: '50px',
                    position: 'fixed',
                    bottom: '50px',
                    right: '10px',
                    ':hover': {
                        background: theme.palette.themeDark,
                        color: theme.palette.white,
                    },
                }
            }}
        />
    }

    const renderButtonPrevNextChange = () => {
        return <IconButton
            iconProps={{ iconName: 'ScrollUpDown' }}
            title="Previous Change (Ctrl + Up) / Next Change (Ctrl + Down)"
            ariaLabel="Previous/Next Change"
            onClick={findNextChange}
            styles={{
                icon: {
                    fontSize: '16px',
                },
                root: {
                    zIndex: (QUICK_NAV_OVERLAY_VISIBLE_STYLE.zIndex as any) - 1,
                    backgroundColor: theme.palette.white,
                    border: '1px solid #20202020',
                    borderRadius: '50px',
                    position: 'fixed',
                    bottom: '130px',
                    right: '10px',
                    ':hover': {
                        background: theme.palette.themeDark,
                        color: theme.palette.white,
                    },
                }
            }}
        />
    }
    const renderButtonRadialNavBookmark = () => {
        return <IconButton
            iconProps={{ iconName: 'RadioBtnOn' }}
            title="Quick Radial Navigation (Shift + Alt)"
            ariaLabel="Quick Radial Navigation"
            onClick={() => setRadialNavIsHidden(false)}
            styles={{
                icon: {
                    fontSize: '16px',
                },
                root: {
                    zIndex: (QUICK_NAV_OVERLAY_VISIBLE_STYLE.zIndex as any) - 1,
                    backgroundColor: theme.palette.white,
                    border: '1px solid #20202020',
                    borderRadius: '50px',
                    position: 'fixed',
                    bottom: '90px',
                    right: '10px',
                    ':hover': {
                        background: theme.palette.themeDark,
                        color: theme.palette.white,
                    },
                }
            }}
        />
    }


    const renderCompareQuickNav = () => {
        return (
            <PharmacyQuickNavOverlay
                primePharmacyModel={compareViewModel}
                isHidden={quickNavIsHidden}
                markers={markers}
                onNavigate={handleQuickNavNavigate}
                showDifferencesOnly={showDifferencesOnly}
                onDismiss={() => setQuickNavIsHidden(true)}
            />
        );
    }

    const renderCompareRadialNav = () => {
        return (
            <PharmacyRadialNavOverlay
                primePharmacyModel={compareViewModel}
                isHidden={radialNavIsHidden}
                markers={markers}
                onNavigate={handleQuickNavNavigate}
                showDifferencesOnly={showDifferencesOnly}
                onDismiss={() => setRadialNavIsHidden(true)}
            />
        );
    }


    const renderEntityCompare = (entityVM: IEntityVM) => {

        if (!configuration)
            return null;

        if (!shouldIncludeComparisonItem(showDifferencesOnly, entityVM.changed))
            return (
                <MessageBar className={fadeIn} messageBarType={MessageBarType.info} styles={{ root: { marginBottom: '10px' } }}>
                    No differences found.
                </MessageBar>
            );

        return (
            <EntityComparePanel
                key={entityVM.uniqueId}
                compareVM={entityVM}
                stickyOffset={STICKY_HEADER_HEIGHT}
                configuration={configuration}
                classNames={classNames}
                markers={markers}
                authentication={authentication}
                isPharmacyApp={true}
            />
        );
    }

    const renderCompareStatus = () => {

        // has there been any comparison?
        if (!compareViewModel?.prime)
            return <></>;

        if (compareViewModel.prime.compareResult === ECompareVMStatus.Identical || compareViewModel.prime.compareResult === ECompareVMStatus.Modified)
            return <></>;

        return (
            <MessageBar className={fadeIn} messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: '10px' } }}>
                Compare was not able to locate a matching benefit on origin for this benefit.
            </MessageBar>
        );

    }

    const renderCompareSummary = () => {
        const summary = compareViewModel?.summary;

        if (!summary || !configuration)
            return null;

        if (!shouldIncludeComparisonItem(showDifferencesOnly, summary.changed))
            return null;

        const children: JSX.Element[] = [];

        summary.rowsVM.forEach((property) => {

            if (!shouldIncludeComparisonItem(showDifferencesOnly, property.changed))
                return;

            children.push(
                <CompareRow
                    key={property.propertyDefinition.name}
                    name={<CompareTitle
                        changed={property.changed}
                        name={property.propertyDefinition.name}
                        entityProps={{ markers }}
                    />}
                    valueVM={{
                        origin: property.origin,
                        target: property.target,
                        changed: property.changed,
                        mergeAction: property.mergeAction,
                    }}
                    entityProps={{}}
                />
            );
        })

        return (
            <ComparePanel
                id={'summary-panel'}
                title={<CompareTitle
                    changed={summary.changed}
                    name={'Summary'}
                    entityProps={{ markers }}
                />}
                canCollapse={true}
                defaultCollapsed={getDefaultCollapsed(configuration, summary.changed)}
                stickyHeader={true}
                stickyTop={0}
                stickyZIndex={100}
                styleType={EPanelStyleType.Default}
            >
                <CompareTable
                    className={classNames}
                    tableStyles={TABLE_STYLES}
                    originHeader={ORIGIN_TITLE}
                    targetHeader={TARGET_TITLE}
                    hideActionHeader={true}
                >
                    {children}
                </CompareTable>
            </ComparePanel>
        );

    }

    const renderCompareBenefit = () => {
        const prime = compareViewModel?.prime;

        if (!prime || !configuration)
            return null;

        return (
            <ComparePanel
                id={BENEFIT_COMPARISON_PANEL_ID}
                title={<CompareTitle
                    changed={prime.changed}
                    name={'Benefit Comparison'}
                    entityProps={{ markers }}
                />}
                canCollapse={true}
                defaultCollapsed={getDefaultCollapsed(configuration, prime.changed)}
                stickyHeader={true}
                stickyTop={0}
                stickyZIndex={90}
                styleType={EPanelStyleType.Default}
            >
                <Stack {...STACK_DEFAULT_PROPS}>
                    {renderEntityCompare(prime)}
                </Stack>
            </ComparePanel>
        );
    }

    const renderCompareGroupNumber = () => {
        const groupNumbers = compareViewModel?.groupNumbers;

        if (!groupNumbers || !configuration)
            return null;

        if (!shouldIncludeComparisonItem(showDifferencesOnly, groupNumbers.changed))
            return null;

        return (
            <ComparePanel
                id={GROUP_NUMBER_COMPARISON_PANEL_ID}
                title={<CompareTitle
                    changed={groupNumbers.changed}
                    name={'Group Numbers Comparison'}
                    entityProps={{ markers }}
                />}
                canCollapse={true}
                defaultCollapsed={getDefaultCollapsed(configuration, groupNumbers.changed)}
                stickyHeader={true}
                stickyTop={0}
                stickyZIndex={90}
                styleType={EPanelStyleType.Default}
            >
                <Stack {...STACK_DEFAULT_PROPS}>
                    {groupNumbers.entities.map((eachGroupNumberVM) => renderEntityCompare(eachGroupNumberVM))}
                </Stack>
            </ComparePanel>
        )
    }

    const renderCompareView = () => {
        return (
            <Stack {...STACK_DEFAULT_PROPS}>
                {renderCompareSummary()}
                {renderCompareBenefit()}
                {renderCompareGroupNumber()}
            </Stack>
        );
    };

    const renderFloatingActionButtons = () => {
        return (
            <>
                {renderButtonQuickNavBookmark()}
                {renderButtonPrevNextChange()}
                {renderButtonRadialNavBookmark()}
            </>
        );
    };

    return (
        <Stack>
            {hasUnsavedChanges &&
                <MessageBar className={fadeIn} messageBarType={MessageBarType.error} styles={{ root: { marginBottom: '10px' } }}>
                    <Text variant='medium'>
                        {comparisonState
                            ? "This comparison is out of sync with your latest changes. Consider saving, then rerunning the comparison for the most accurate results."
                            : "Unsaved changes detected."
                        }
                    </Text>
                </MessageBar>
            }
            {renderCompareStatus()}
            {renderFloatingActionButtons()}
            {!comparisonState && renderFAMView()}
            {comparisonState && renderCompareView()}
            {renderCompareQuickNav()}
            {renderCompareRadialNav()}
        </Stack>
    )
};

export default PharmacyPlan;
